import styled from "styled-components";
import { Flex } from "antd";

// Only the gaps Ant Design can't cover; text/layout use antd Typography/Space.
export const PageWrapperStyled = styled.div`
  padding: 24px;
  max-width: 900px;
  margin: 0 auto;
  transition: filter 0.25s ease;
  ${p => p.$blurred && `
    filter: blur(4px);
    pointer-events: none;
    user-select: none;
  `}
`;

/* Top action bar holding the compliance/consent buttons (matches the HTML bar). */
export const ActionBarStyled = styled(Flex)`
  margin-bottom: 16px;
  padding: 12px 20px;
  background: ${p => p.theme?.colors?.white ?? "#ffffff"};
  border: 1px solid ${p => p.theme?.colors?.border ?? "#D8DAE5"};
  border-radius: 8px;
`;

/* Wrapper that turns the SbButton inside into the green "Compliance Active" badge. */
export const ComplianceButtonWrapperStyled = styled.span`
  .ant-btn {
    font-weight: 700;
    color: ${p => p.theme?.colors?.success ?? "#28a745"};
    border-color: ${p => p.theme?.colors?.successBorder ?? "#52BD94"};
    background: ${p => p.theme?.colors?.successBg ?? "#F5FBF8"};
  }
  .ant-btn:hover {
    color: ${p => p.theme?.colors?.success ?? "#28a745"} !important;
    border-color: ${p => p.theme?.colors?.successBorder ?? "#52BD94"} !important;
    background: ${p => p.theme?.colors?.successBgStrong ?? "#E6F4EA"} !important;
  }
`;

/* Bottom bar shown while previewing the terms during first-login onboarding. */
export const PreviewBarStyled = styled(Flex)`
  position: fixed;
  left: 0;
  bottom: 0;
  width: 100%;
  z-index: 1100;
  padding: 14px 24px;
  color: ${p => p.theme?.colors?.white ?? "#ffffff"};
  background: ${p => p.theme?.colors?.dark ?? "#2B3034"};
  border-top: 4px solid ${p => p.theme?.colors?.primary ?? "#008080"};
`;

/* Branded header banner for the legal document (theme primary).
   Rendered as the SbCard `cover` — full-bleed, rounded top to match the card. */
export const TermsHeaderStyled = styled.div`
  position: relative;
  padding: 24px 28px;
  border-radius: 8px 8px 0 0;
  background: ${p => p.theme?.colors?.primary ?? "#008080"};

  &::after {
    content: "";
    position: absolute;
    left: 0;
    bottom: 0;
    width: 100%;
    height: 4px;
    background: ${p => p.theme?.colors?.secondary ?? "#DDEAEA"};
  }

  /* PDF: drop the colored banner and render the header as plain text. */
  @media print {
    padding: 0 0 10px;
    margin-bottom: 16px;
    border-radius: 0;
    background: transparent !important;
    border-bottom: 1px solid ${p => p.theme?.colors?.borderLight ?? "#E5E7EB"};
    &::after { display: none; }
  }
`;

export const TermsHeaderTitleStyled = styled.h1`
  margin: 0 0 6px;
  font-size: 16px;
  font-weight: 700;
  line-height: 1.3;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  color: ${p => p.theme?.colors?.white ?? "#ffffff"};

  @media print {
    color: ${p => p.theme?.colors?.dark ?? "#2B3034"} !important;
  }
`;

export const TermsHeaderMetaStyled = styled(Flex)`
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid rgba(255, 255, 255, 0.25);
  font-size: 12px;
  color: ${p => p.theme?.colors?.secondary ?? "#DDEAEA"};

  strong {
    color: ${p => p.theme?.colors?.white ?? "#ffffff"};
    font-weight: 600;
  }

  @media print {
    border-top-color: ${p => p.theme?.colors?.borderLight ?? "#E5E7EB"};
    color: ${p => p.theme?.colors?.gray ?? "#565E70"} !important;
    strong { color: ${p => p.theme?.colors?.dark ?? "#2B3034"} !important; }
  }
`;


/* Body content below the banner (padding comes from the SbCard body). */
export const TermsContentStyled = styled.div`
  font-size: 14px;
  line-height: 1.6;
  color: ${p => p.theme?.colors?.dark ?? "#2B3034"};

  p { margin: 0 0 12px; font-size: 14px; line-height: 1.6; text-align: left; }
  ul { margin: 0 0 12px; padding-left: 22px; }
  li { margin: 0 0 6px; font-size: 14px; line-height: 1.6; text-align: left; }
  strong { font-weight: 600; }
  a { color: ${p => p.theme?.colors?.primary ?? "#008080"}; }

  table { width: 100%; border-collapse: collapse; margin: 14px 0; font-size: 13px; }
  th, td {
    border: 1px solid ${p => p.theme?.colors?.border ?? "#D8DAE5"};
    padding: 9px;
    line-height: 1.5;
    text-align: left;
    vertical-align: top;
  }
  th {
    background: ${p => p.theme?.colors?.light ?? "#F7F7F7"};
    color: ${p => p.theme?.colors?.primary ?? "#008080"};
    font-weight: 700;
  }
`;

export const ExclusionBoxStyled = styled.div`
  margin: 18px 0;
  padding: 12px 16px;
  border: 1px solid ${p => p.theme?.colors?.warningBorder ?? "#FFB020"};
  border-left: 4px solid ${p => p.theme?.colors?.warningBorder ?? "#FFB020"};
  border-radius: 4px;
  background: ${p => p.theme?.colors?.warningBg ?? "#FFFAF1"};
  color: ${p => p.theme?.colors?.warningMessage ?? "#996A13"};

  strong { color: ${p => p.theme?.colors?.warningMessage ?? "#996A13"}; }
`;

export const IntroLeadStyled = styled.p`
  font-weight: 700;
  color: ${p => p.theme?.colors?.primary ?? "#008080"};
`;

export const NoticeBoxStyled = styled.div`
  margin: 18px 0;
  padding: 12px 16px;
  border-left: 4px solid ${p => p.theme?.colors?.primary ?? "#008080"};
  background: ${p => p.theme?.colors?.light ?? "#F7F7F7"};
  border-radius: 0 4px 4px 0;
`;

export const ArticleHeadingStyled = styled.h3`
  margin: 24px 0 12px;
  padding-bottom: 6px;
  font-size: 15px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: ${p => p.theme?.colors?.primary ?? "#008080"};
  border-bottom: 2px solid ${p => p.theme?.colors?.borderLight ?? "#E5E7EB"};
`;

export const SignoffStyled = styled.div`
  margin-top: 28px;
  padding-top: 14px;
  border-top: 2px solid ${p => p.theme?.colors?.primary ?? "#008080"};
  font-style: italic;
  color: ${p => p.theme?.colors?.gray ?? "#565E70"};
`;

// ─── Consent modal ────────────────────────────────────────────────────────────

/* Inline link for opening the Platform Terms from the consent form. */
export const TermsLinkStyled = styled.a`
  color: ${p => p.theme?.colors?.primary ?? "#008080"};
  font-weight: 600;
  cursor: pointer;
  &:hover { text-decoration: underline; }
`;

/* Gray "geographic configurator" box grouping the location + consent choices. */
export const GeoBoxStyled = styled(Flex)`
  padding: 16px;
  border: 1px solid ${p => p.theme?.colors?.border ?? "#D8DAE5"};
  border-radius: 8px;
  background: ${p => p.theme?.colors?.light ?? "#F7F7F7"};
`;

/* Each consent choice rendered as its own bordered section. */
export const ConsentCardStyled = styled.div`
  padding: 12px 14px;
  border: 1px solid ${p => p.theme?.colors?.border ?? "#D8DAE5"};
  border-radius: 8px;
  background: ${p => p.theme?.colors?.white ?? "#ffffff"};
  transition: background 0.15s;
  &:hover { background: ${p => p.theme?.colors?.light ?? "#F7F7F7"}; }

  .ant-checkbox { align-self: flex-start; margin-top: 3px; }
  .ant-checkbox-wrapper { align-items: flex-start; }
`;

export const FootnoteStyled = styled.p`
  margin: 16px 0 0;
  font-size: 12px;
  font-style: italic;
  color: ${p => p.theme?.colors?.tertiary ?? "#8790A4"};
`;

/* Loading / load-error state on the Legal page. */
export const PageStatusStyled = styled(Flex)`
  min-height: 42vh;
  padding: 24px;
`;

/* Recovery screen shown when the user declines the terms. */
export const DeclineScreenStyled = styled(Flex)`
  min-height: 42vh;
  padding: 24px;
  text-align: center;
`;

export const DeclineIconStyled = styled(Flex)`
  width: 64px;
  height: 64px;
  border-radius: 50%;
  font-size: 28px;
  color: ${p => p.theme?.colors?.danger ?? "#DC2626"};
  background: ${p => p.theme?.colors?.warningBg ?? "#FFFAF1"};
`;
